import React from "react"
import { useTranslation } from "react-i18next"

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@f2e/ui/components/base"

import { ClientChannelType } from "@/types"

import {
  CHANNEL_SOURCE_FILTER_VALUES,
  getChannelSourceLabelKey,
} from "../../history-source-labels"

export type SourceFilterValue =
  | "all"
  | "workspace_api_key"
  | "client_channel"
  | ClientChannelType

export interface SourceSelectProps {
  value?: SourceFilterValue
  compact?: boolean
  onSourceChange?: (value: SourceFilterValue) => void
}

const SourceSelect = ({
  value = "all",
  compact = false,
  onSourceChange,
}: SourceSelectProps) => {
  const { t } = useTranslation()
  const triggerId = "history-source-select"

  return (
    <div
      className={compact ? "grid gap-1.5" : "mb-4 flex items-center space-x-4"}
    >
      <label
        htmlFor={triggerId}
        className={
          compact ? "text-xs font-medium text-muted-foreground" : "font-medium"
        }
      >
        {t("historyList.sourceSelect.label")}
      </label>
      <Select
        value={value}
        onValueChange={(nextValue) =>
          onSourceChange?.(nextValue as SourceFilterValue)
        }
      >
        <SelectTrigger
          id={triggerId}
          className={compact ? "h-9 w-full min-w-0 text-sm" : "w-[220px]"}
        >
          <SelectValue
            placeholder={t("historyList.sourceSelect.placeholder")}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="pl-6">
            {t("historyList.sourceSelect.all")}
          </SelectItem>
          <SelectGroup>
            <SelectLabel>
              {t("historyList.sourceSelect.groups.internal")}
            </SelectLabel>
            <SelectItem value="workspace_api_key" className="pl-6">
              {t("historyList.sourceSelect.workspaceApi")}
            </SelectItem>
          </SelectGroup>
          <SelectGroup>
            <SelectLabel>
              {t("historyList.sourceSelect.groups.channels")}
            </SelectLabel>
            <SelectItem value="client_channel" className="pl-6">
              {t("historyList.sourceSelect.allChannels")}
            </SelectItem>
            {CHANNEL_SOURCE_FILTER_VALUES.map((channelType) => (
              <SelectItem
                key={channelType}
                value={channelType}
                className="pl-6"
              >
                {t(
                  `historyList.sourceSelect.options.${getChannelSourceLabelKey(channelType)}`
                )}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}

export default React.memo(SourceSelect)
